import React, { ReactElement } from 'react';

import {
  NumberParameterValue,
  NumberPrimitiveParameterValue,
} from '~/scenes/Automation/Flows/Actions/Base/types.flow';
import { WithBaseActionContextProps } from '~/scenes/Automation/Flows/Actions/BaseActionContext';
import { WithControlledSwitcherProps } from '~/scenes/Automation/Flows/Actions/Base/types.flow';

import { PARAMETER_VALUE_TYPE } from '~/scenes/Automation/Flows/Actions/Base/FlowParameter/ParameterValue/constants';
import { Dropdown } from '~/components/Inputs';
import Catalog from '~/Catalog';
import { emptyNumberPrimitiveParameterValue } from '~/scenes/Automation/Flows/Actions/Base/FlowParameter/ParameterValue';
import NumberPrimitiveParameterValueComponent from './NumberPrimitiveParameterValueComponent';
import TEST_ID from './NumberParameterValueComponent.testid';
import FlowContext from '~/scenes/Automation/Flows/FlowContext';
import { getStashVariablesInStash } from '~/scenes/Automation/Flows/Actions/util/stashHelpers';
import { withBaseActionContext } from '~/scenes/Automation/Flows/Actions/BaseActionContext';
import { FLOW_OUTPUT_TYPE } from '~/util/constants';

import cleanedFilename from '~/util/cleanedFilename';
import getVariableWithPrimitiveDropdownProps from '~/scenes/Automation/Flows/Actions/Base/FlowParameter/ParameterValue/util/getVariableWithPrimitiveDropdownProps';
import getFieldDropdownProps from '~/scenes/Automation/Flows/Actions/Base/FlowParameter/ParameterValue/util/getFieldDropdownProps';
import validateStashVariable from '../util/validateStashVariable';
import AddParameterValueButton from '../AddParameterValueButton';

const text = {
  variableLabel: Catalog.flows.pointerVariableLabel,
  fieldLabel: Catalog.flows.pointerFieldLabel,
  requiredField: Catalog.requiredField,
};
type MyProps = {
  allowNull?: boolean;
  value: NumberParameterValue;
  change: WithControlledSwitcherProps<NumberParameterValue>;
};
type Props = WithBaseActionContextProps & MyProps;
type State = {
  key: string | null;
  stateValue: NumberParameterValue | null;
  primitiveValueCache: NumberPrimitiveParameterValue;
};
class NumberParameterValueComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { value } = this.props;

    let stateValue: NumberParameterValue | null = null;
    let defaultPrimitiveValueCache =
      value != null && value.type === PARAMETER_VALUE_TYPE.NUMBER_PRIMITIVE
        ? value
        : emptyNumberPrimitiveParameterValue();

    if (props.change.controlled) {
      if (
        props.value != null &&
        props.value.type === PARAMETER_VALUE_TYPE.NUMBER_PRIMITIVE
      ) {
        defaultPrimitiveValueCache = props.value;
      }
    } else {
      stateValue = props.value;
    }

    this.state = {
      key: null,
      stateValue,
      primitiveValueCache: defaultPrimitiveValueCache,
    };
  }

  componentDidMount() {
    const { baseActionContext } = this.props;

    const key = baseActionContext.subscribeValidator({
      validate: this.validate,
    });

    this.setState({
      key,
    });
  }

  componentWillUnmount() {
    const { baseActionContext } = this.props;

    if (this.state.key != null) {
      baseActionContext.unsubscribeValidator(this.state.key);
    }
  }

  getValue = (): NumberParameterValue => {
    if (this.props.change.controlled) {
      return this.props.value;
    } else {
      if (this.state.stateValue == null) {
        throw Error(
          `${cleanedFilename(
            __filename,
          )} | Should not occur | stateValue is null`,
        );
      }
      return this.state.stateValue;
    }
  };

  setValue = (newValue: NumberParameterValue) => {
    if (this.props.change.controlled) {
      this.props.change.onChange(newValue);
    } else {
      this.setState({
        stateValue: newValue,
      });
    }
  };

  setPrimitiveValueCache = (
    newPrimitiveValue: NumberPrimitiveParameterValue,
  ) => {
    this.setState({
      primitiveValueCache: newPrimitiveValue,
    });
  };

  validatePrimitive = (): boolean => {
    const { value, allowNull } = this.props;

    if (value == null || value.type !== PARAMETER_VALUE_TYPE.NUMBER_PRIMITIVE) {
      throw Error(
        `${cleanedFilename(
          __filename,
        )} Should not occur | validatePrimitive should only get a primitive value type value to check`,
      );
    }

    if (allowNull) {
      return true;
    } else {
      return value.value != null;
    }
  };

  validate = (): boolean => {
    const { value, allowNull } = this.props;
    if (this.props.change.controlled) {
      if (value == null) {
        return allowNull || true;
      } else if (value.type === PARAMETER_VALUE_TYPE.NUMBER_PRIMITIVE) {
        return this.validatePrimitive();
      } else {
        return validateStashVariable(
          value.variable,
          this.getPossibleVariables(),
          FLOW_OUTPUT_TYPE.FlowOutputNumber,
        );
      }
    } else {
      return true;
    }
  };

  getPossibleVariables = () => {
    const { baseActionContext } = this.props;
    const { variableStash } = baseActionContext;

    return getStashVariablesInStash(
      variableStash,
      undefined,
      FLOW_OUTPUT_TYPE.FlowOutputNumber,
    );
  };

  handleAdded = (
    newPrimitiveValue?: NumberPrimitiveParameterValue | null | undefined,
  ) => {
    let newValue = this.getValue();

    if (newPrimitiveValue != null) {
      newValue = newPrimitiveValue;
    }

    if (this.props.change.controlled || !this.props.change.onAdded) {
      throw Error(
        `${cleanedFilename(
          __filename,
        )} | Should not occur | Showed the AddParameterValueButton even though onAdded was not given`,
      );
    } else {
      this.props.change.onAdded(newValue);
    }
  };

  render() {
    const { primitiveValueCache } = this.state;
    const value = this.getValue();
    const shouldShowAddButton = !this.props.change.controlled;

    const {
      variableOptions,
      selectedVariableIdx,
      selectedVariableOption,
      onChangeVariable,
    } = getVariableWithPrimitiveDropdownProps<NumberParameterValue>(
      this.getPossibleVariables(),
      value,
      () => {
        this.setValue({ ...primitiveValueCache });
      },
      newVariableName => {
        this.setValue({
          type: PARAMETER_VALUE_TYPE.NUMBER_POINTER,
          variable: { name: newVariableName, field: null },
        });
      },
    );

    return (
      <FlowContext.Consumer>
        {({ showValidation }) => {
          let inputComponent: ReactElement | null = null;
          let dropdownComponent: ReactElement | null = null;

          if (value != null) {
            if (value.type === PARAMETER_VALUE_TYPE.NUMBER_PRIMITIVE) {
              if (this.props.change.controlled) {
                inputComponent = (
                  <NumberPrimitiveParameterValueComponent
                    error={
                      showValidation && !this.validatePrimitive()
                        ? text.requiredField
                        : undefined
                    }
                    value={value}
                    change={{
                      controlled: true,
                      onChange: newPrimitiveValue => {
                        this.setPrimitiveValueCache(newPrimitiveValue);
                        this.setValue(newPrimitiveValue);
                      },
                    }}
                  />
                );
              } else {
                inputComponent = (
                  <NumberPrimitiveParameterValueComponent
                    error={
                      showValidation && !this.validatePrimitive()
                        ? text.requiredField
                        : undefined
                    }
                    value={value}
                    change={{
                      controlled: false,
                      onAdded: newPrimitiveValue => {
                        this.handleAdded(newPrimitiveValue);
                      },
                      announceChanges: this.props.change.announceChanges,
                    }}
                  />
                );
              }
              {
                /*               
              inputComponent = (
              <NumberPrimitiveParameterValueComponent
                error={
                  showValidation && !this.validatePrimitive()
                    ? text.requiredField
                    : undefined
                }
                value={value}
                onChange={newPrimitiveValue => {
                  this.setPrimitiveValueCache(newPrimitiveValue);
                  onChange(newPrimitiveValue);
                }}
              />
              ); */
              }
            } else if (value.type === PARAMETER_VALUE_TYPE.NUMBER_POINTER) {
              const {
                fieldOptions,
                selectedFieldIdx,
                selectedFieldOption,
                onChangeField,
              } = getFieldDropdownProps(
                selectedVariableOption,
                value,
                (newFieldName: string) => {
                  if (value.variable == null) {
                    throw Error(
                      `${cleanedFilename(
                        __filename,
                      )} | Should not occur | Changing field even though variable has not been chosen yet!`,
                    );
                  }

                  const newValue: NumberParameterValue = {
                    type: PARAMETER_VALUE_TYPE.NUMBER_POINTER,
                    variable: {
                      name: value.variable.name,
                      field: {
                        name: newFieldName,
                      },
                    },
                  };

                  this.setValue(newValue);
                },
                FLOW_OUTPUT_TYPE.FlowOutputNumber,
                FLOW_OUTPUT_TYPE.FlowOutputNumber,
              );

              inputComponent = (
                <>
                  <Dropdown
                    dataTestid={TEST_ID.FIELD_DROPDOWN}
                    selectOnlyOptionAutomatically
                    error={
                      showValidation && selectedFieldOption == null
                        ? text.requiredField
                        : ''
                    }
                    label={text.fieldLabel}
                    options={fieldOptions}
                    selectedOptionIdx={selectedFieldIdx}
                    onChange={onChangeField}
                  />
                  <AddParameterValueButton
                    isVisible={shouldShowAddButton}
                    onClick={this.handleAdded}
                  />
                </>
              );

              /**
               * Pointers in the comparison are a legacy option
               * Show the dropdown to change to primitive, but only if the pointer already existed
               */
              dropdownComponent = (
                <Dropdown
                  dataTestid={TEST_ID.VARIABLE_DROPDOWN}
                  selectOnlyOptionAutomatically
                  error={
                    showValidation && selectedVariableOption == null
                      ? Catalog.requiredField
                      : ''
                  }
                  label={text.variableLabel}
                  options={variableOptions}
                  selectedOptionIdx={selectedVariableIdx}
                  onChange={onChangeVariable}
                />
              );
            }
          }

          return (
            <>
              {dropdownComponent}
              {inputComponent}
            </>
          );
        }}
      </FlowContext.Consumer>
    );
  }
}

export default withBaseActionContext<MyProps>(NumberParameterValueComponent);
